package cz.lhoracek.mapl.ui.screens.newsession

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.IconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import org.koin.compose.viewmodel.koinViewModel
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.layout.Box
import androidx.compose.ui.tooling.preview.Preview
import cz.lhoracek.mapl.ui.theme.MaplTheme
import cz.lhoracek.mapl.ui.hexToColor
import cz.lhoracek.mapl.ui.components.PlayerAvatar as CommonPlayerAvatar
import cz.lhoracek.mapl.ui.components.MaplTopAppBar
import cz.lhoracek.mapl.ui.components.NumberStepper
import mapl.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import cz.lhoracek.mapl.domain.nextBetPowerOfTwo
import cz.lhoracek.mapl.domain.previousBetPowerOfTwo

@Composable
fun NewSessionScreen(
    onBack: () -> Unit,
    onSessionCreated: (String) -> Unit,
) {
    val viewModel: NewSessionViewModel = koinViewModel()
    val state by viewModel.uiState.collectAsState()

    DisposableEffect(Unit) {
        onDispose {
            viewModel.resetForm()
        }
    }

    LaunchedEffect(state.createdSessionId) {
        state.createdSessionId?.let { id ->
            viewModel.consumeNavigation()
            viewModel.resetForm()
            onSessionCreated(id)
        }
    }

    NewSessionContent(
        state = state,
        onBack = {
            viewModel.resetForm()
            onBack()
        },
        onSessionNameChange = viewModel::setSessionName,
        onUpdatePlayer = { index, name, color -> viewModel.updatePlayer(index, name, color) },
        onAddPlayer = viewModel::addPlayer,
        onRemovePlayer = viewModel::removePlayer,
        onBaseBetChange = viewModel::setBaseBet,
        onPotEnabledChange = viewModel::setPotEnabled,
        onCreateSession = viewModel::createSession,
        onDismissError = viewModel::dismissError,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NewSessionContent(
    state: NewSessionUiState,
    onBack: () -> Unit,
    onSessionNameChange: (String) -> Unit,
    onUpdatePlayer: (Int, String, String) -> Unit,
    onAddPlayer: () -> Unit,
    onRemovePlayer: (Int) -> Unit,
    onBaseBetChange: (Int) -> Unit,
    onPotEnabledChange: (Boolean) -> Unit,
    onCreateSession: () -> Unit,
    onDismissError: () -> Unit,
) {
    val snackbarHostState = remember { SnackbarHostState() }
    val focusManager = LocalFocusManager.current
    val playerFocusRequesters = remember { List(4) { FocusRequester() } }

    LaunchedEffect(state.error) {
        state.error?.let {
            snackbarHostState.showSnackbar(it)
            onDismissError()
        }
    }
    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            MaplTopAppBar(
                title = stringResource(Res.string.new_session_title),
                onBack = onBack,
            )
        },
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .padding(horizontal = 16.dp)
                .verticalScroll(rememberScrollState())
                .imePadding(),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            Spacer(Modifier.height(8.dp))

            OutlinedTextField(
                value = state.sessionName,
                onValueChange = onSessionNameChange,
                label = { Text(stringResource(Res.string.new_session_name)) },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
                keyboardActions = KeyboardActions(onNext = {
                    playerFocusRequesters[0].requestFocus()
                })
            )

            Text(stringResource(Res.string.new_session_players), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)

            state.players.forEachIndexed { index, player ->
                PlayerRow(
                    index = index,
                    player = player,
                    canRemove = state.players.size > 3,
                    onNameChange = { onUpdatePlayer(index, it, player.color) },
                    onColorChange = { onUpdatePlayer(index, player.name, it) },
                    onRemove = { onRemovePlayer(index) },
                    focusRequester = playerFocusRequesters[index],
                    imeAction = if (index < state.players.size - 1) ImeAction.Next else ImeAction.Done,
                    onImeAction = {
                        if (index < state.players.size - 1) {
                            playerFocusRequesters[index + 1].requestFocus()
                        } else {
                            focusManager.clearFocus()
                        }
                    }
                )
            }

            if (state.players.size < 4) {
                FilledTonalButton(
                    onClick = onAddPlayer,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text("+ " + stringResource(Res.string.new_session_add_player))
                }
            }

            HorizontalDivider()

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text(stringResource(Res.string.new_session_bet), style = MaterialTheme.typography.bodyLarge)
                NumberStepper(
                    valueLabel = "${state.baseBet}",
                    onDecrement = { onBaseBetChange(previousBetPowerOfTwo(state.baseBet)) },
                    onIncrement = { onBaseBetChange(nextBetPowerOfTwo(state.baseBet)) },
                    decrementEnabled = state.baseBet > 1,
                    valueContent = {
                        Text(
                            "${state.baseBet}",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                        )
                    },
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text(stringResource(Res.string.new_session_pot_toggle), style = MaterialTheme.typography.bodyLarge)
                Switch(
                    checked = state.potEnabled,
                    onCheckedChange = onPotEnabledChange,
                )
            }

            Spacer(Modifier.height(8.dp))

            Button(
                onClick = onCreateSession,
                modifier = Modifier.fillMaxWidth().height(52.dp),
                enabled = !state.isCreating && state.players.all { it.name.isNotBlank() },
            ) {
                if (state.isCreating) {
                    CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                } else {
                    Text(stringResource(Res.string.new_session_create))
                }
            }

            Spacer(Modifier.height(16.dp))
        }
    }
}

@Composable
private fun PlayerRow(
    index: Int,
    player: PlayerDraft,
    canRemove: Boolean,
    onNameChange: (String) -> Unit,
    onColorChange: (String) -> Unit,
    onRemove: () -> Unit,
    focusRequester: FocusRequester,
    imeAction: ImeAction,
    onImeAction: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        CommonPlayerAvatar(
            name = player.name,
            colorHex = player.color,
            size = 48.dp
        )
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                OutlinedTextField(
                    value = player.name,
                    onValueChange = onNameChange,
                    label = { Text(stringResource(Res.string.new_player_name) + " ${index + 1}") },
                    modifier = Modifier.weight(1f).focusRequester(focusRequester),
                    singleLine = true,
                    isError = player.name.isBlank(),
                    keyboardOptions = KeyboardOptions(imeAction = imeAction),
                    keyboardActions = KeyboardActions(
                        onNext = { onImeAction() },
                        onDone = { onImeAction() }
                    )
                )
                if (canRemove) {
                    IconButton(onClick = onRemove) {
                        Text(stringResource(Res.string.remove), color = MaterialTheme.colorScheme.error)
                    }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                PLAYER_COLORS.forEach { hex ->
                    val selected = player.color == hex
                    Box(
                        modifier = Modifier
                            .size(28.dp)
                            .background(hexToColor(hex), CircleShape)
                            .border(
                                width = if (selected) 3.dp else 1.dp,
                                color = if (selected) MaterialTheme.colorScheme.onSurface
                                        else MaterialTheme.colorScheme.outline.copy(alpha = 0.3f),
                                shape = CircleShape,
                            )
                            .clickable { onColorChange(hex) },
                    )
                }
            }
        }
    }
}

@Preview
@Composable
private fun NewSessionPreview() {
    MaplTheme {
        NewSessionContent(
            state = NewSessionUiState(
                sessionName = "Páteční mariáš",
                players = listOf(
                    PlayerDraft("1", "Honza", PLAYER_COLORS[0]),
                    PlayerDraft("2", "Pepa", PLAYER_COLORS[1]),
                    PlayerDraft("3", "Jirka", PLAYER_COLORS[2]),
                    PlayerDraft("4", "Mirek", PLAYER_COLORS[3]),
                ),
                baseBet = 2,
            ),
            onBack = {},
            onSessionNameChange = {},
            onUpdatePlayer = { _, _, _ -> },
            onAddPlayer = {},
            onRemovePlayer = {},
            onBaseBetChange = {},
            onPotEnabledChange = {},
            onCreateSession = {},
            onDismissError = {},
        )
    }
}
